import {flow, types} from 'mobx-state-tree';
import * as api from '../../services/api';
import ListingVariantModel from '../ListingVariantModel';
import PaginationModel from '../PaginationModel';

export enum VariantType {
  LONG_TERM = 'LONG_TERM'
}

// tslint:disable
export const ListingModel = types
  .model('ListingModel', {
    id: types.identifierNumber,
    name: types.string,
    internalName: types.optional(types.string, ''),
    source: types.optional(types.string, ''),
    address: types.optional(types.string, ''),
    teamId: types.maybe(types.number),
    otaListingId: types.maybe(types.string),
    otaListingUrl: types.maybe(types.string),
    images: types.array(
      types.model({
        url: types.optional(types.string, ''),
        small: types.optional(types.string, '')
      })
    ),
    variants: types.array(ListingVariantModel),
    members: types.map(
      types.model({
        id: types.identifierNumber,
        firstName: types.string,
        lastName: types.optional(types.string, ''),
        email: types.maybe(types.string),
        avatar: types.maybe(types.string),
        role: types.maybe(
          types.model({
            id: types.identifierNumber,
            name: types.string,
            code: types.string
          })
        )
      })
    ),
    team: types.model({
      id: types.identifierNumber,
      name: types.string
    }),
    joined: types.optional(types.boolean, false)
  })
  .views(self => ({
    get previewImageUrl() {
      const {images} = self;
      return images && images.length > 0
        ? images[0].small || images[0].url
        : undefined;
    },
    get longTermVariant() {
      return self.variants.find(
        variant => variant.type == VariantType.LONG_TERM
      );
    }
  }))
  .actions(self => ({
    tooogleJoined: () => {
      self.joined = !Boolean(self.joined);
    },
    removeMember: (userId: number) => {
      self.members.delete(String(userId));
    },
    assignMember: (user: any) => {
      self.members.put(user);
    }
  }));

export type ListingModelType = typeof ListingModel.Type;

export type ListingsListActions = {
  loadEntries: (offset?: number, limit?: number) => void;
  // tslint:disable-next-line:no-any
  receiveEntries: (data: any) => void;
  loadNextPage: () => void;
  loadPreviousPage: () => void;
};

const ListingsListModel = types
  .model('ListingsListModel', {
    isListingLoading: types.optional(types.boolean, false),
    isListingLoaded: types.optional(types.boolean, false),
    isLoading: types.optional(types.boolean, false),
    isLoaded: types.optional(types.boolean, false),
    isLoadingAddNewListing: types.optional(types.boolean, false),
    pagination: types.optional(PaginationModel, {}),
    entries: types.map(ListingModel),
    currentListing: types.maybeNull(ListingModel),
    newListing: types.optional(
      types.model({
        teamId: types.maybe(types.number),
        name: types.maybe(types.string),
        address: types.maybe(types.string),
        timezoneName: types.maybe(types.string),
        countryCode: types.maybe(types.string),
        bedrooms: types.maybe(types.integer),
        internalName: types.maybe(types.string),
        propertyType: types.maybe(types.string),
        summary: types.maybe(types.string),
        checkInTime: types.maybe(types.string),
        checkOutTime: types.maybe(types.string),
        source: types.maybe(types.string),
        otaListingId: types.maybe(types.string),
        image: types.maybe(types.string),
        primaryHostName: types.maybe(types.string)
      }),
      {}
    )
  })
  .actions(self => {
    // tslint:disable-next-line:no-any
    const withActions = (self as any) as ListingsListActions;
    return {
      // tslint:disable-next-line:no-any
      loadEntries: flow(function*(params: any) {
        self.isLoading = true;
        const response = yield api.getListings(params);
        if (response.status === 200 && response.data) {
          const {offset, limit, total} = response.data.metadata;
          self.pagination.offset = offset;
          self.pagination.limit = limit;
          self.pagination.total = total;

          withActions.receiveEntries(response.data.listings);

          self.isLoading = false;
          self.isLoaded = true;
        }
      }),
      loadNextPage: function() {
        const {page, nextOffset, limit} = self.pagination;
        page(nextOffset);

        const params = {
          _offset: nextOffset,
          _limit: limit,
          long_term: 'true'
        };

        this.loadEntries(params);
      },
      loadPreviousPage: function() {
        const {page, previousOffset, limit} = self.pagination;
        page(previousOffset);

        const params = {
          _offset: previousOffset,
          _limit: limit,
          long_term: 'true'
        };

        this.loadEntries(params);
      },
      loadEntry: flow(function*(listingId: number) {
        self.isListingLoading = true;
        self.isListingLoaded = false;

        const format = 'variant';
        const response = yield api.getListing(listingId, format);
        if (response.status === 200 && response.data) {
          const listing = response.data.listing;

          if (listing.members) {
            listing.members = listing.members.reduce((cs: any, c: any) => {
              cs[String(c.id)] = c;
              return cs;
            }, {});
          }

          self.currentListing = listing;
        } else {
          self.currentListing = null;
        }

        self.isListingLoading = false;
        self.isListingLoaded = true;
      }),
      unsetEntry: () => {
        self.currentListing = null;
        self.isListingLoaded = false;
      },
      // tslint:disable-next-line:no-any
      receiveEntries: (entries: any) => {
        if (!entries || entries.length === 0) {
          return;
        }

        const received = entries.reduce((ts: any, t: any) => {
          t.members = (t.members || []).reduce((cs: any, c: any) => {
            cs[String(c.id)] = c;
            return cs;
          }, {});

          ts[String(t.id)] = t;
          return ts;
        }, {});

        self.entries.merge(received);
      },
      updateStateListingsWithJoinState: (newUserId: number | undefined) => {
        self.isLoading = true;
        self.entries.forEach((listing: any, key: string) => {
          let membersListings = listing.members;
          if (membersListings.length > 0) {
            let memberIds = membersListings.map(
              (member: any, idx: number) => member.id
            );
            listing.joined = false;
            if (newUserId && memberIds.indexOf(newUserId) !== -1) {
              listing.joined = true;
            }
          }
        });
        self.isLoading = false;
      },
      toogleJoined: (listingId: number) => {
        const listing = self.entries.get(String(listingId));
        if (listing) {
          listing.joined = !listing.joined;
        }
      },
      getJoined: (listingId: number) => {
        const listing = self.entries.get(String(listingId));
        return listing ? listing.joined : false;
      },
      createListing: flow(function*(body: any) {
        self.isLoadingAddNewListing = true;
        const response = yield api.createListing(body);
        if (response.status === 200 && response.data) {
          let listing = response.data.listing;
          listing.members = listing.members.reduce((cs: any, c: any) => {
            cs[String(c.id)] = c;
            return cs;
          }, {});

          self.entries.set(String(listing.id), listing);
        }
        self.isLoadingAddNewListing = false;
      }),
      onChangeNameListing: (name: string) => {
        self.newListing.name = name;
      },
      onChangeAddressListing: (address: string) => {
        self.newListing.address = address;
      },
      onChangeTeamIdListing: (teamId: number) => {
        self.newListing.teamId = teamId;
      },
      onChangeTimezoneNameListing: (timezoneName: string) => {
        self.newListing.timezoneName = timezoneName;
      },
      onChangeCountryCodeListing: (countryCode: string) => {
        self.newListing.countryCode = countryCode;
      },
      onChangePrimaryHostNameListing: (primaryHostName: string) => {
        self.newListing.primaryHostName = primaryHostName;
      },
      onChangeBedroomsListing: (bedrooms: number) => {
        self.newListing.bedrooms = bedrooms;
      },
      onChangeInternalNameListing: (internalName: string) => {
        self.newListing.internalName = internalName;
      },
      onChangePropertyTypeListing: (propertyType: string) => {
        self.newListing.propertyType = propertyType;
      },
      onChangeSummaryListing: (summary: string) => {
        self.newListing.summary = summary;
      },
      onChangeCheckInTimeListing: (checkInTime: string) => {
        self.newListing.checkInTime = checkInTime;
      },
      onChangeCheckOutTimeListing: (checkOutTime: string) => {
        self.newListing.checkOutTime = checkOutTime;
      },
      onChangeSourceListing: (source: string) => {
        self.newListing.source = source;
      },
      onChangeOtaListingId: (otaListingId: string) => {
        self.newListing.otaListingId = otaListingId;
      },
      onChangeImageListing: (image: string) => {
        self.newListing.image = image;
      },
      clearEntries: () => {
        self.entries.clear();
      }
    };
  })
  .views(self => ({
    get entriesList() {
      return Array.from(self.entries.values());
    },
    get totalCount() {
      return self.pagination.total;
    },
    get pagedList() {
      const {offset, nextOffset} = self.pagination;
      return this.entriesList.slice(offset, nextOffset);
    }
  }));

export default ListingsListModel;

export type ListingsListModelType = typeof ListingsListModel.Type;



// WEBPACK FOOTER //
// ./src/models/ListingsListModel/ListingsListModel.ts